import { Drawer, Stack, Typography } from "@mui/material"
import { Player } from "@lottiefiles/react-lottie-player"
import { makeStyles } from "tss-react/mui"

import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import GeneralErrorAnim from "../../../../../../shared/assets/general-error-lottie-anim.json"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { getAdminDetails, getUserDetails, UserQuery } from "../../../../../users/userApi"

import { toCompanyAdminDetails, toCompanyUserDetails } from "../companyUserDetailsMapper"
import { CompanyUserDetailsType } from "../companyUserDetailsTypes"
import { CompanyAdminUsersQuery } from "../usersApi"
import { CompanyUserDetailsBody } from "./CompanyUserDetailsBody"
import { CompanyUserDetailsHeader } from "./CompanyUserDetailsHeader"

type CompanyUserDetailsDrawerProps = Readonly<{
    userId: number
    detailsType: CompanyUserDetailsType
    isActive: boolean
    open: boolean
    onClose: () => void
    onEdit: () => void
    onImpersonate?: () => void
}>

const useStyles = makeStyles()((theme) => ({
    paper: {
        width: "34rem",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
    },
    stateBox: {
        flex: 1,
        minHeight: 0,
        padding: "1.5rem",
        textAlign: "center",
        background: theme.palette.tailwind.slate[100],
    },
    errorImage: {
        width: "12rem",
    },
    errorText: {
        color: theme.palette.tailwind.slate[500],
    },
}))

export function CompanyUserDetailsDrawer({
    userId,
    detailsType,
    isActive,
    open,
    onClose,
    onEdit,
    onImpersonate,
}: CompanyUserDetailsDrawerProps) {
    const { classes } = useStyles()

    const userDetailsResult = useApiQuery(getUserDetails, {
        queryName: CompanyAdminUsersQuery.USER_DETAILS,
        dependencies: { userId },
        enabled: open && detailsType === CompanyUserDetailsType.USER,
        errorDisplay: ErrorDisplay.NONE,
        select: toCompanyUserDetails,
    })

    const adminDetailsResult = useApiQuery(getAdminDetails, {
        queryName: UserQuery.ADMIN_DETAILS,
        dependencies: { userId },
        enabled: open && detailsType === CompanyUserDetailsType.ADMIN,
        errorDisplay: ErrorDisplay.NONE,
        select: toCompanyAdminDetails,
    })

    const detailsResult = detailsType === CompanyUserDetailsType.ADMIN ? adminDetailsResult : userDetailsResult
    const user = detailsResult.data

    const renderContent = () => {
        if (detailsResult.isError) {
            return (
                <Stack alignItems="center" justifyContent="center" spacing="1rem" className={classes.stateBox}>
                    <Player autoplay loop controls={false} src={GeneralErrorAnim} className={classes.errorImage} />
                    <LocalizedStrict id="company-user-details-error-title">
                        <Typography variant="subtitle1">Couldn't load user</Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="company-user-details-error-message">
                        <Typography variant="body2" className={classes.errorText}>
                            Something went wrong while loading this user. Please try again.
                        </Typography>
                    </LocalizedStrict>
                </Stack>
            )
        }

        if (!user) {
            return (
                <Stack alignItems="center" justifyContent="center" className={classes.stateBox}>
                    <LoadingSpinner />
                </Stack>
            )
        }

        return <CompanyUserDetailsBody user={user} onEdit={onEdit} onImpersonate={onImpersonate} />
    }

    return (
        <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
            <CompanyUserDetailsHeader user={user} isActive={isActive} onClose={onClose} />
            {renderContent()}
        </Drawer>
    )
}
